// APIC Token Service
// Handles OAuth token fetching, caching, and authenticated API calls
import config from '../config/appConfig';

let cachedToken = null;
let tokenExpiry = null;
// In-flight token request promise — prevents duplicate simultaneous token fetches
let tokenPromise = null;

/**
 * Fetch a new OAuth token from APIC
 */
const fetchNewToken = async () => {
  const tokenUrl = (import.meta.env.DEV && window.location.hostname === 'localhost')
    ? `/api-proxy/identitymanagement/qapath1/v3/auth/oauth2/spa/app/token`
    // 'qapath1' -> 'stress' for qa: QA now points at the APIC Stress environment (see
    // appConfig.js's APIC_BASE_URL/APIC_BASE_URL_API comment for the source email) —
    // token URL there is .../identitymanagement/stress/v3/auth/oauth2/spa/app/token,
    // not .../identitymanagement/qapath1/.... 'dev' is untouched since the email only
    // covers QA, and prod already has its own separate 'prod' branch below.
    : `${config.APIC_BASE_URL}/identitymanagement/${config.environment === 'qa' ? 'stress' : config.environment === 'dev' ? 'qapath1' : 'prod'}/v3/auth/oauth2/spa/app/token`;

  // Read client ID from config (populated from VITE_APIC_CLIENT_ID or window.APP_CONFIG)
  const clientId = config.VITE_APIC_CLIENT_ID
    || import.meta.env.VITE_APIC_CLIENT_ID
    || window.APP_CONFIG?.VITE_APIC_CLIENT_ID
    || '';

  console.log('🔐 Fetching new APIC token:', { url: tokenUrl, timestamp: new Date().toISOString() });

  const response = await fetch(tokenUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: new URLSearchParams({
      grant_type: 'client_credentials',
      client_id: clientId,
      scope: 'Public'
    }),
  });

  if (!response.ok) {
    throw new Error(`Token request failed: ${response.status} ${response.statusText}`);
  }

  const data = await response.json();
  console.log('✅ APIC token received, expires_in:', data.expires_in, 's');
  return data;
};

/**
 * Get a valid token.
 * Returns cached token if still valid (with 30s buffer); otherwise fetches a new one.
 * De-duplicates concurrent requests — only one token fetch in-flight at a time.
 */
export const getToken = async () => {
  const now = Date.now();

  // Return cached token if still valid (30s buffer before expiry)
  if (cachedToken && tokenExpiry && now < tokenExpiry - 30000) {
    return cachedToken;
  }

  // If a token fetch is already in flight, wait for it
  if (tokenPromise) {
    return tokenPromise;
  }

  tokenPromise = fetchNewToken()
    .then((tokenData) => {
      cachedToken  = tokenData.access_token;
      // expires_in is in seconds; convert to ms
      tokenExpiry  = Date.now() + tokenData.expires_in * 1000;
      tokenPromise = null;
      return cachedToken;
    })
    .catch((err) => {
      tokenPromise = null;
      cachedToken  = null;
      tokenExpiry  = null;
      console.error('❌ Failed to obtain APIC token:', err.message);
      throw err;
    });

  return tokenPromise;
};

/**
 * Clears the cached token (useful for forced re-authentication)
 */
export const clearToken = () => {
  cachedToken  = null;
  tokenExpiry  = null;
  tokenPromise = null;
};

/**
 * Authenticated fetch wrapper.
 * - Automatically attaches Bearer token to every request.
 * - On 401, clears the cache and retries once with a fresh token.
 *
 * @param {string} url - The API endpoint URL
 * @param {RequestInit} options - Fetch options (method, body, headers, etc.)
 * @returns {Promise<Response>}
 */
export const apicFetch = async (url, options = {}) => {
  const token = await getToken();

  const headers = {
    'Content-Type': 'application/json',
    ...( options.headers || {} ),
    'Authorization': `Bearer ${token}`,
  };

  const response = await fetch(url, { ...options, headers });

  // If token was rejected, clear cache and retry once
  if (response.status === 401) {
    console.warn('⚠️ 401 received — refreshing APIC token and retrying...');
    clearToken();
    const freshToken = await getToken();
    return fetch(url, {
      ...options,
      headers: { ...headers, 'Authorization': `Bearer ${freshToken}` },
    });
  }

  return response;
};
